/**
 * Story viewer (sections/story-viewer.liquid)
 * <story-viewer> wraps a <dialog>. Opened by any [data-story-open] element (event delegation),
 * so other sections can open stories without extra code. Optional [data-story-index].
 *
 * Live site behaviour kept: per-story duration, segmented progress, pause/play,
 * session resume, closes after the last story, images load only when opened.
 * Instagram behaviour added: 3D cube / slide swipe that follows the finger,
 * tap left/right to go back/forward, press-and-hold to pause, swipe down to close.
 *
 * Events: dispatches 'story-viewer:ready', 'story-viewer:open', 'story-viewer:close' on document.
 */
(() => {
  if (customElements.get('story-viewer')) return;

  const STORAGE_KEY = 'theme:story-viewer:index';
  const HOLD_DELAY = 180;
  const MOVE_THRESHOLD = 10;
  const SWIPE_RATIO = 0.2;
  const SWIPE_VELOCITY = 0.4;
  const CLOSE_DISTANCE = 110;
  const TAP_ZONE = 0.3;
  const MOBILE_QUERY = '(max-width: 950px)';

  const storage = {
    get() {
      try {
        return Number.parseInt(sessionStorage.getItem(STORAGE_KEY) ?? '', 10);
      } catch {
        return Number.NaN;
      }
    },
    set(value) {
      try {
        sessionStorage.setItem(STORAGE_KEY, String(value));
      } catch {
        // Storage blocked - resume only works for this page.
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  };

  class StoryViewer extends HTMLElement {
    /** @type {HTMLDialogElement | null} */
    #dialog = null;
    /** @type {HTMLElement[]} */
    #slides = [];
    /** @type {HTMLElement[]} */
    #segments = [];
    #index = 0;
    #paused = false;
    #holding = false;
    #muted = true;
    #closing = false;
    #pointer = null;
    #holdTimer = 0;
    #controller = new AbortController();
    /** @type {ResizeObserver | null} */
    #resizeObserver = null;

    connectedCallback() {
      this.#dialog = this.querySelector('[data-story-dialog]');
      this.#slides = Array.from(this.querySelectorAll('[data-story-slide]'));
      this.#segments = Array.from(this.querySelectorAll('[data-story-segment]'));
      document.dispatchEvent(new CustomEvent('story-viewer:ready'));
      if (!this.#dialog || this.#slides.length === 0) return;

      const { signal } = this.#controller;
      const stage = this.querySelector('[data-story-stage]');

      document.addEventListener('click', this.#onOpenClick.bind(this), { signal });
      this.querySelector('[data-story-close]')?.addEventListener('click', () => this.close(), { signal });
      this.querySelector('[data-story-pause]')?.addEventListener('click', () => this.togglePause(), { signal });
      this.querySelector('[data-story-sound]')?.addEventListener('click', () => this.#toggleSound(), { signal });
      this.querySelector('[data-story-prev]')?.addEventListener('click', () => this.previous(), { signal });
      this.querySelector('[data-story-next]')?.addEventListener('click', () => this.next(), { signal });
      this.#dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.#dialog.addEventListener('keydown', this.#onKeydown.bind(this), { signal });

      stage?.addEventListener('pointerdown', this.#onPointerDown.bind(this), { signal });
      stage?.addEventListener('pointermove', this.#onPointerMove.bind(this), { signal });
      stage?.addEventListener('pointerup', this.#onPointerUp.bind(this), { signal });
      stage?.addEventListener('pointercancel', this.#onPointerCancel.bind(this), { signal });
      stage?.addEventListener('contextmenu', (event) => event.preventDefault(), { signal });

      for (const segment of this.#segments) {
        segment.addEventListener('animationend', () => {
          if (segment.classList.contains('is-current')) this.next();
        }, { signal });
      }

      document.addEventListener('visibilitychange', () => {
        if (!this.#dialog?.open) return;
        if (document.hidden) this.#pauseMedia(true);
        else if (!this.#paused) this.#pauseMedia(false);
      }, { signal });

      const frame = this.querySelector('[data-story-frame]');
      if (frame) {
        this.#resizeObserver = new ResizeObserver(() => {
          this.#dialog.style.setProperty('--story-viewer-cube-depth', `${frame.clientWidth / 2}px`);
        });
        this.#resizeObserver.observe(frame);
      }
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      this.#resizeObserver?.disconnect();
      clearTimeout(this.#holdTimer);
    }

    /** @param {number} [index] */
    open(index) {
      if (!this.#dialog || this.#dialog.open) return;
      const saved = storage.get();
      let start = index ?? (this.dataset.remember === 'true' && !Number.isNaN(saved) ? saved : 0);
      if (start < 0 || start >= this.#slides.length) start = 0;

      this.#closing = false;
      this.#dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.#dialog.classList.add('is-jumping');
      this.#show(start);
      requestAnimationFrame(() => {
        this.#dialog.classList.remove('is-jumping');
        this.#dialog.classList.add('is-open');
      });
      document.dispatchEvent(new CustomEvent('story-viewer:open', { detail: { index: start } }));
    }

    close() {
      if (!this.#dialog?.open || this.#closing) return;
      this.#closing = true;
      this.#pauseMedia(true);
      this.#dialog.classList.remove('is-open', 'is-pulling');
      this.#dialog.style.removeProperty('--story-viewer-overlay-strength');

      const finish = () => {
        this.#dialog.close();
        this.#dialog.classList.remove('is-paused');
        document.documentElement.style.removeProperty('overflow');
        this.#closing = false;
        document.dispatchEvent(new CustomEvent('story-viewer:close'));
      };
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        finish();
        return;
      }
      setTimeout(finish, 350);
    }

    next() {
      if (this.#index >= this.#slides.length - 1) {
        // Live behaviour: after the last story, close and start from the beginning next time.
        storage.clear();
        this.close();
        return;
      }
      this.#show(this.#index + 1);
    }

    previous() {
      // Instagram behaviour: on the first story, restart it.
      this.#show(Math.max(this.#index - 1, 0));
    }

    togglePause() {
      this.#setPaused(!this.#paused);
    }

    /** @param {number} index */
    #show(index) {
      const previous = this.#slides[this.#index];
      this.#index = index;
      this.#layout();
      this.#hydrate(index);
      this.#hydrate(index + 1);

      for (const video of previous?.querySelectorAll('video') ?? []) video.pause();

      const slide = this.#slides[index];
      const video = slide.querySelector('video');
      const duration = Number(slide.dataset.duration) || 10;

      for (const [i, segment] of this.#segments.entries()) {
        segment.classList.toggle('is-done', i < index);
        segment.classList.remove('is-current');
      }
      const current = this.#segments[index];
      if (current) {
        current.style.setProperty('--story-duration', `${duration}s`);
        void current.offsetWidth; // restart the CSS animation
        current.classList.add('is-current');
      }

      const sound = this.querySelector('[data-story-sound]');
      if (sound) sound.hidden = !video;
      if (video) {
        video.muted = this.#muted;
        video.currentTime = 0;
        video.play().catch(() => {});
      }

      this.#setPaused(false);
      if (this.dataset.remember === 'true') storage.set(index);

      const status = this.querySelector('[data-story-status]');
      if (status) {
        status.textContent = (this.dataset.labelStory || '')
          .replace('[index]', String(index + 1))
          .replace('[count]', String(this.#slides.length));
      }
    }

    /** Positions slides relative to the current one; CSS turns --story-offset into slide/cube transforms. */
    #layout(dragPx = 0) {
      const frame = this.querySelector('[data-story-frame]');
      const width = frame?.clientWidth || 1;
      for (const [i, slide] of this.#slides.entries()) {
        const offset = i - this.#index;
        slide.style.setProperty('--story-offset', String(offset));
        slide.classList.toggle('is-near', Math.abs(offset) <= 1);
        slide.inert = offset !== 0;
      }
      const track = this.querySelector('[data-story-track]');
      track?.style.setProperty('--story-drag', `${dragPx}px`);
      track?.style.setProperty('--story-drag-deg', `${(dragPx / width) * 90}deg`);
    }

    /** @param {number} index */
    #hydrate(index) {
      const slide = this.#slides[index];
      if (!slide) return;
      for (const template of slide.querySelectorAll('template[data-story-video]')) {
        template.replaceWith(template.content.cloneNode(true));
      }
      for (const image of slide.querySelectorAll('img[data-src]')) {
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      }
      for (const video of slide.querySelectorAll('video[preload="none"]')) video.preload = 'auto';
    }

    /** @param {boolean} paused */
    #setPaused(paused) {
      this.#paused = paused;
      const button = this.querySelector('[data-story-pause]');
      button?.setAttribute('aria-pressed', String(paused));
      button?.setAttribute('aria-label', (paused ? this.dataset.labelPlay : this.dataset.labelPause) || '');
      this.#pauseMedia(paused);
    }

    /** @param {boolean} paused */
    #pauseMedia(paused) {
      this.#dialog?.classList.toggle('is-paused', paused);
      const video = this.#slides[this.#index]?.querySelector('video');
      if (!video) return;
      if (paused) video.pause();
      else video.play().catch(() => {});
    }

    #toggleSound() {
      this.#muted = !this.#muted;
      const button = this.querySelector('[data-story-sound]');
      button?.setAttribute('aria-pressed', String(!this.#muted));
      button?.setAttribute('aria-label', (this.#muted ? this.dataset.labelUnmute : this.dataset.labelMute) || '');
      for (const video of this.querySelectorAll('video')) video.muted = this.#muted;
    }

    /** @param {MouseEvent} event */
    #onOpenClick(event) {
      const trigger = event.target instanceof Element ? event.target.closest('[data-story-open]') : null;
      if (!trigger) return;
      event.preventDefault();
      const index = Number.parseInt(trigger.getAttribute('data-story-index') ?? '', 10);
      this.open(Number.isNaN(index) ? undefined : index);
    }

    /** @param {KeyboardEvent} event */
    #onKeydown(event) {
      const isRtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        isRtl ? this.previous() : this.next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        isRtl ? this.next() : this.previous();
      } else if (event.key === ' ' && !(event.target instanceof HTMLButtonElement)) {
        event.preventDefault();
        this.togglePause();
      }
    }

    /* ---------- Gestures ---------- */

    /** @param {PointerEvent} event */
    #onPointerDown(event) {
      if (event.button !== 0 || !(event.target instanceof Element)) return;
      if (event.target.closest('a, button')) return;

      event.currentTarget.setPointerCapture?.(event.pointerId);
      this.#pointer = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: performance.now(),
        mode: 'pending',
        dx: 0,
        dy: 0
      };
      this.#holding = false;
      clearTimeout(this.#holdTimer);
      this.#holdTimer = setTimeout(() => {
        if (this.#pointer?.mode !== 'pending') return;
        this.#holding = true;
        this.#pauseMedia(true);
      }, HOLD_DELAY);
    }

    /** @param {PointerEvent} event */
    #onPointerMove(event) {
      const pointer = this.#pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      pointer.dx = event.clientX - pointer.startX;
      pointer.dy = event.clientY - pointer.startY;

      if (pointer.mode === 'pending') {
        const absX = Math.abs(pointer.dx);
        const absY = Math.abs(pointer.dy);
        if (absX < MOVE_THRESHOLD && absY < MOVE_THRESHOLD) return;
        clearTimeout(this.#holdTimer);
        pointer.mode = absX > absY ? 'swipe' : pointer.dy > 0 ? 'pull' : 'ignore';
        this.#pauseMedia(true);
        if (pointer.mode === 'swipe') this.#dialog.classList.add('is-dragging');
        if (pointer.mode === 'pull') this.#dialog.classList.add('is-pulling');
      }

      if (pointer.mode === 'swipe') {
        const atStart = this.#index === 0 && pointer.dx > 0;
        const atEnd = this.#index === this.#slides.length - 1 && pointer.dx < 0;
        const resisted = atStart || atEnd ? pointer.dx * 0.3 : pointer.dx;
        this.#layout(resisted);
      } else if (pointer.mode === 'pull') {
        const pull = Math.max(pointer.dy, 0);
        const progress = Math.min(pull / window.innerHeight, 1);
        this.#dialog.style.setProperty('--story-viewer-pull', `${pull}px`);
        this.#dialog.style.setProperty('--story-viewer-pull-scale', String(1 - progress * 0.25));
        this.#dialog.style.setProperty('--story-viewer-overlay-strength', String(1 - progress));
      }
    }

    /** @param {PointerEvent} event */
    #onPointerUp(event) {
      const pointer = this.#pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      clearTimeout(this.#holdTimer);
      this.#pointer = null;

      const elapsed = Math.max(performance.now() - pointer.startTime, 1);
      const frame = this.querySelector('[data-story-frame]');
      const width = frame?.clientWidth || window.innerWidth;

      if (pointer.mode === 'swipe') {
        this.#dialog.classList.remove('is-dragging');
        const velocity = pointer.dx / elapsed;
        const passed = Math.abs(pointer.dx) > width * SWIPE_RATIO || Math.abs(velocity) > SWIPE_VELOCITY;
        this.#layout(0);
        if (passed && pointer.dx < 0) {
          this.next();
          return;
        }
        if (passed && pointer.dx > 0 && this.#index > 0) {
          this.previous();
          return;
        }
        if (!this.#paused) this.#pauseMedia(false);
        return;
      }

      if (pointer.mode === 'pull') {
        const velocity = pointer.dy / elapsed;
        if (pointer.dy > CLOSE_DISTANCE || velocity > SWIPE_VELOCITY) {
          this.close();
          return;
        }
        this.#dialog.classList.remove('is-pulling');
        this.#dialog.style.removeProperty('--story-viewer-overlay-strength');
        if (!this.#paused) this.#pauseMedia(false);
        return;
      }

      if (this.#holding) {
        this.#holding = false;
        if (!this.#paused) this.#pauseMedia(false);
        return;
      }

      if (pointer.mode !== 'pending') return;

      // Tap zones (mobile, live site: 30% each side)
      if (!window.matchMedia(MOBILE_QUERY).matches) return;
      const ratio = event.clientX / window.innerWidth;
      const isRtl = getComputedStyle(this).direction === 'rtl';
      if (ratio < TAP_ZONE) {
        isRtl ? this.next() : this.previous();
      } else if (ratio > 1 - TAP_ZONE) {
        isRtl ? this.previous() : this.next();
      }
    }

    #onPointerCancel() {
      clearTimeout(this.#holdTimer);
      this.#pointer = null;
      this.#holding = false;
      this.#dialog?.classList.remove('is-dragging', 'is-pulling');
      this.#dialog?.style.removeProperty('--story-viewer-overlay-strength');
      this.#layout(0);
      if (!this.#paused) this.#pauseMedia(false);
    }
  }

  customElements.define('story-viewer', StoryViewer);
})();
